import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/entityLock.mutation"

import type { AcquireResult } from "../types"
import { toEntityLock } from "../utils"

import type {
  AcquireEntityLockMutation,
  AcquireEntityLockMutationVariables,
  EntityLockInput,
} from "@/graphql/codegen/graphql"
import { AcquireEntityLockDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseAcquireEntityLockReturn {
  acquireLock: (input: EntityLockInput) => Promise<AcquireResult>
  isAcquiring: boolean
}

export const useAcquireEntityLock = (): UseAcquireEntityLockReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<AcquireEntityLockMutation, AcquireEntityLockMutationVariables>(
    AcquireEntityLockDocument
  )

  const acquireLock = useCallback(
    async (input: EntityLockInput): Promise<AcquireResult> => {
      const { data, error } = await executeMutation({ input })

      if (error) {
        notify({ type: NotificationType.Error, message: error.message })
        return { ok: false, currentLock: null }
      }

      const output = data?.entityLock.acquire
      if (output?.__typename === "AcquireEntityLockSuccess") {
        const lock = toEntityLock(output.lock)
        if (lock) return { ok: true, lock }
        return { ok: false, currentLock: null }
      }

      if (output?.__typename === "AcquireEntityLockFailure") {
        return { ok: false, currentLock: toEntityLock(output.error.currentLock) ?? null }
      }

      return { ok: false, currentLock: null }
    },
    [executeMutation, notify]
  )

  return { acquireLock, isAcquiring: fetching }
}
